/**
 * Canonical Intelligence Graph (CIG) Schema
 * 
 * This is the "truth model" that represents the structured intelligence
 * extracted from research reports. All views and blocks render from this
 * canonical representation.
 * 
 * @version 2.2
 */

import { z } from 'zod'

// ============================================================================
// Base Types
// ============================================================================

export const EvidenceAnchorSchema = z.object({
  source_id: z.string(),
  source_url: z.string().optional(),
  locator: z.string().optional(), // page/chunk/char range
  snippet: z.string().optional(),
  support_type: z.enum(['supports', 'contradicts', 'context']).default('supports'),
  reliability_score: z.number().min(0).max(1).optional(),
  timestamp: z.string().datetime().optional(),
})
export type EvidenceAnchor = z.infer<typeof EvidenceAnchorSchema>

export const ConfidenceLevelSchema = z.enum(['very_low', 'low', 'medium', 'high', 'very_high'])
export type ConfidenceLevel = z.infer<typeof ConfidenceLevelSchema>

export const RiskPostureSchema = z.enum(['critical', 'high', 'elevated', 'moderate', 'low', 'minimal'])
export type RiskPosture = z.infer<typeof RiskPostureSchema>

// ============================================================================
// Entity Schema
// ============================================================================

export const EntityTypeSchema = z.enum([
  'person',
  'organization',
  'law_regulation',
  'location',
  'concept',
  'molecule_protein',
  'dataset',
  'event',
  'product',
  'technology',
])
export type EntityType = z.infer<typeof EntityTypeSchema>

export const EntitySchema = z.object({
  id: z.string(),
  type: EntityTypeSchema,
  name: z.string(),
  description: z.string().optional(),
  aliases: z.array(z.string()).optional(),
  tags: z.array(z.string()).optional(),
  metadata: z.record(z.unknown()).optional(),
})
export type Entity = z.infer<typeof EntitySchema>

// ============================================================================
// Claim Schema
// ============================================================================

export const ClaimSchema = z.object({
  id: z.string(),
  text: z.string(),
  confidence: ConfidenceLevelSchema,
  evidence_anchors: z.array(EvidenceAnchorSchema).min(1), // MUST have at least one
  counterpoint: z.string().optional(),
  key_assumption: z.string().optional(),
  category: z.string().optional(),
  related_entities: z.array(z.string()).optional(), // entity IDs
})
export type Claim = z.infer<typeof ClaimSchema>

// ============================================================================
// Metric Schema
// ============================================================================

export const MetricValueSchema = z.union([
  z.number(),
  z.string(),
  z.object({
    value: z.number(),
    formatted: z.string().optional(),
  }),
])
export type MetricValue = z.infer<typeof MetricValueSchema>

export const ScenarioBindingSchema = z.object({
  by_state: z.record(z.string(), MetricValueSchema), // state_id -> value
})
export type ScenarioBinding = z.infer<typeof ScenarioBindingSchema>

export const MetricSchema = z.object({
  id: z.string(),
  label: z.string(),
  value: MetricValueSchema,
  unit: z.string().optional(),
  timeframe: z.string().optional(),
  trend: z.enum(['up', 'down', 'stable', 'volatile']).optional(),
  trend_value: z.number().optional(), // percentage change
  confidence: ConfidenceLevelSchema,
  evidence_anchors: z.array(EvidenceAnchorSchema).min(1),
  scenario_binding: ScenarioBindingSchema.optional(),
  category: z.string().optional(),
})
export type Metric = z.infer<typeof MetricSchema>

// ============================================================================
// Relationship Schema
// ============================================================================

export const RelationshipTypeSchema = z.enum([
  'funded',
  'sued',
  'regulates',
  'located_in',
  'cited',
  'competes_with',
  'causes',
  'partners_with',
  'owns',
  'employs',
  'produces',
  'depends_on',
  'opposes',
  'supports_entity',
  'related_to',
])
export type RelationshipType = z.infer<typeof RelationshipTypeSchema>

export const RelationshipSchema = z.object({
  id: z.string(),
  source_entity_id: z.string(),
  target_entity_id: z.string(),
  type: RelationshipTypeSchema,
  label: z.string().optional(),
  strength: z.number().min(0).max(1).optional(),
  evidence_anchors: z.array(EvidenceAnchorSchema).optional(),
  metadata: z.record(z.unknown()).optional(),
})
export type Relationship = z.infer<typeof RelationshipSchema>

// ============================================================================
// Source Schema
// ============================================================================

export const ReliabilitySchema = z.object({
  score: z.number().min(0).max(1),
  tier: z.enum(['primary', 'secondary', 'tertiary']).optional(),
  rationale: z.string(),
})
export type Reliability = z.infer<typeof ReliabilitySchema>

export const SourceExcerptSchema = z.object({
  id: z.string(),
  text: z.string(),
  page: z.number().optional(),
  char_start: z.number().optional(),
  char_end: z.number().optional(),
})
export type SourceExcerpt = z.infer<typeof SourceExcerptSchema>

export const SourceSchema = z.object({
  id: z.string(),
  title: z.string(),
  publisher: z.string().optional(),
  authors: z.array(z.string()).optional(),
  date: z.string().optional(), // ISO date or human-readable
  url: z.string().url().optional(),
  anchor_text: z.string().optional(),
  reliability: ReliabilitySchema,
  excerpts: z.array(SourceExcerptSchema).optional(),
  source_type: z.enum([
    'academic_paper',
    'news_article',
    'government_doc',
    'company_report',
    'legal_filing',
    'book',
    'website',
    'interview',
    'data_source',
    'other',
  ]).optional(),
})
export type Source = z.infer<typeof SourceSchema>

// ============================================================================
// Scenario Schema
// ============================================================================

// Per-state trigger (matches backend ScenarioTrigger in scenario.py)
export const StateSpecificTriggerSchema = z.object({
  trigger_id: z.string(),
  description: z.string(),
  observability: z.string().optional(),
  confidence: z.number().min(0).max(1).optional(),
})
export type StateSpecificTrigger = z.infer<typeof StateSpecificTriggerSchema>

// Per-state assumption (matches backend ScenarioAssumption in scenario.py)
export const StateSpecificAssumptionSchema = z.object({
  assumption_id: z.string(),
  text: z.string(),
  rationale: z.string().optional(),
})
export type StateSpecificAssumption = z.infer<typeof StateSpecificAssumptionSchema>

export const ScenarioStateSchema = z.object({
  state_id: z.string(),
  label: z.string(),
  short_description: z.string().optional(),
  is_default: z.boolean().optional(),
  // Per-state data from Scenario Agent (schema v2.2)
  probability_band: z.string().optional(),
  triggers: z.array(StateSpecificTriggerSchema).optional(),
  assumptions: z.array(StateSpecificAssumptionSchema).optional(),
  metric_impacts: z.array(z.string()).optional(),
})
export type ScenarioState = z.infer<typeof ScenarioStateSchema>

export const ScenarioTriggerSchema = z.object({
  id: z.string(),
  label: z.string(),
  definition: z.string(),
  indicator_type: z.enum(['metric', 'event', 'threshold']).optional(),
  thresholds: z.record(z.string(), z.unknown()).optional(),
  maps_to_state_transitions: z.array(z.object({
    from_state: z.string().optional(),
    to_state: z.string(),
    condition: z.string().optional(),
  })).optional(),
  confidence: ConfidenceLevelSchema.optional(),
  evidence_anchors: z.array(EvidenceAnchorSchema).optional(),
})
export type ScenarioTrigger = z.infer<typeof ScenarioTriggerSchema>

export const ScenarioAssumptionSchema = z.object({
  id: z.string(),
  text: z.string(),
  applies_to_state_id: z.string().optional(), // if null, applies to all states
  evidence_anchors: z.array(EvidenceAnchorSchema).optional(),
})
export type ScenarioAssumption = z.infer<typeof ScenarioAssumptionSchema>

export const ScenarioAxisSchema = z.object({
  axis_id: z.string(),
  label: z.string(),
  description: z.string().optional(),
  states: z.array(ScenarioStateSchema).min(2).max(7),
  triggers: z.array(ScenarioTriggerSchema).optional(),
  assumptions: z.array(ScenarioAssumptionSchema).optional(),
  default_state_by_persona: z.record(z.string(), z.string()).optional(), // persona_id -> state_id
})
export type ScenarioAxis = z.infer<typeof ScenarioAxisSchema>

// ============================================================================
// Section Schema (for Written Report)
// ============================================================================

export const SectionContentBlockSchema = z.object({
  type: z.enum(['paragraph', 'heading', 'list', 'quote', 'callout', 'figure_ref']),
  content: z.string(),
  level: z.number().optional(), // for headings: 1, 2, 3
  items: z.array(z.string()).optional(), // for lists
  citation_refs: z.array(z.string()).optional(), // source IDs for citations in this block
})
export type SectionContentBlock = z.infer<typeof SectionContentBlockSchema>

// Define the type first for recursive reference
interface ReportSectionType {
  id: string
  title: string
  level: number
  one_line_summary?: string
  content_blocks: z.infer<typeof SectionContentBlockSchema>[]
  subsections?: ReportSectionType[]
}

const baseReportSectionSchema = z.object({
  id: z.string(),
  title: z.string(),
  level: z.number().min(1).max(3),
  one_line_summary: z.string().optional(),
  content_blocks: z.array(SectionContentBlockSchema),
})

export const ReportSectionSchema: z.ZodType<ReportSectionType> = baseReportSectionSchema.extend({
  subsections: z.lazy(() => z.array(ReportSectionSchema)).optional(),
})
export type ReportSection = ReportSectionType

// ============================================================================
// Figure Schema
// ============================================================================

export const FigureTypeSchema = z.enum([
  'line_chart',
  'bar_chart',
  'area_chart',
  'scatter_chart',
  'pie_chart',
  'map',
  'timeline',
  'network_graph',
  'tree_diagram',
  'flow_chart',
  'table',
  'image',
  '3d_model',
])
export type FigureType = z.infer<typeof FigureTypeSchema>

export const FigureSchema = z.object({
  id: z.string(),
  type: FigureTypeSchema,
  title: z.string(),
  caption: z.string(),
  data: z.unknown(), // Renderer-specific data structure
  data_source_ids: z.array(z.string()).optional(),
  evidence_anchors: z.array(EvidenceAnchorSchema).optional(),
  scenario_binding: ScenarioBindingSchema.optional(),
  render_options: z.record(z.unknown()).optional(),
})
export type Figure = z.infer<typeof FigureSchema>

// ============================================================================
// Dataset Schema
// ============================================================================

export const DataColumnSchema = z.object({
  key: z.string(),
  label: z.string(),
  type: z.enum(['string', 'number', 'date', 'boolean', 'currency', 'percentage']),
  format: z.string().optional(),
  unit: z.string().optional(),
})
export type DataColumn = z.infer<typeof DataColumnSchema>

export const DatasetSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional(),
  columns: z.array(DataColumnSchema),
  rows: z.array(z.record(z.unknown())),
  source_ids: z.array(z.string()).optional(),
  last_updated: z.string().datetime().optional(),
})
export type Dataset = z.infer<typeof DatasetSchema>

// ============================================================================
// Decision/Action Schema
// ============================================================================

export const DecisionOptionSchema = z.object({
  id: z.string(),
  label: z.string(),
  description: z.string(),
  pros: z.array(z.string()),
  cons: z.array(z.string()),
  expected_outcomes: z.array(z.string()).optional(),
  risks: z.array(z.string()).optional(),
  evidence_anchors: z.array(EvidenceAnchorSchema).optional(),
  scenario_outcomes: z.record(z.string(), z.string()).optional(), // state_id -> outcome description
})
export type DecisionOption = z.infer<typeof DecisionOptionSchema>

export const WatchlistItemSchema = z.object({
  id: z.string(),
  indicator: z.string(),
  description: z.string().optional(),
  threshold: z.string().optional(),
  current_value: MetricValueSchema.optional(),
  status: z.enum(['normal', 'warning', 'critical']).optional(),
  maps_to_scenario_state: z.string().optional(),
  evidence_anchors: z.array(EvidenceAnchorSchema).optional(),
})
export type WatchlistItem = z.infer<typeof WatchlistItemSchema>

// ============================================================================
// Change Log Schema
// ============================================================================

export const ChangeLogEntrySchema = z.object({
  id: z.string(),
  date: z.string(),
  type: z.enum(['added', 'updated', 'removed', 'corrected']),
  description: z.string(),
  affected_sections: z.array(z.string()).optional(),
})
export type ChangeLogEntry = z.infer<typeof ChangeLogEntrySchema>

// ============================================================================
// Main CIG Schema
// ============================================================================

export const CIGMetadataSchema = z.object({
  schema_version: z.literal('2.2'),
  report_id: z.string(),
  title: z.string(),
  subtitle: z.string().optional(),
  topic_id: z.string().optional(),
  category: z.string().optional(),
  tags: z.array(z.string()).optional(),
  created_at: z.string().datetime(),
  last_updated: z.string().datetime(),
  author: z.object({
    name: z.string(),
    email: z.string().optional(),
    avatar_url: z.string().optional(),
  }).optional(),
  reading_time_minutes: z.number().optional(),
  word_count: z.number().optional(),
  confidence: ConfidenceLevelSchema,
  risk_posture: RiskPostureSchema.optional(),
})
export type CIGMetadata = z.infer<typeof CIGMetadataSchema>

export const CIGSchema = z.object({
  metadata: CIGMetadataSchema,
  
  // Core intelligence
  entities: z.array(EntitySchema),
  claims: z.array(ClaimSchema),
  metrics: z.array(MetricSchema),
  relationships: z.array(RelationshipSchema),
  sources: z.array(SourceSchema),
  
  // Scenario system
  scenario_axes: z.array(ScenarioAxisSchema).optional(),
  
  // Content structure
  bluf: z.string(), // Bottom Line Up Front
  executive_summary: z.array(z.string()), // Bullet points
  key_findings: z.array(z.string()),
  implications: z.array(z.object({
    audience: z.string(),
    items: z.array(z.string()),
  })).optional(),
  
  // Written report structure
  report_sections: z.array(ReportSectionSchema).optional(),
  
  // Visualizations
  figures: z.array(FigureSchema).optional(),
  datasets: z.array(DatasetSchema).optional(),
  
  // Decision support
  decision_options: z.array(DecisionOptionSchema).optional(),
  watchlist: z.array(WatchlistItemSchema).optional(),
  
  // Trust & methods
  methodology: z.string().optional(),
  limitations: z.array(z.string()).optional(),
  open_questions: z.array(z.string()).optional(),
  change_log: z.array(ChangeLogEntrySchema).optional(),
  
  // Raw markdown fallback (for legacy support)
  raw_report_markdown: z.string().optional(),
})
export type CIG = z.infer<typeof CIGSchema>

// ============================================================================
// Validation Helpers
// ============================================================================

export function validateCIG(data: unknown): { success: true; data: CIG } | { success: false; errors: z.ZodError } {
  const result = CIGSchema.safeParse(data)
  if (result.success) {
    return { success: true, data: result.data }
  }
  return { success: false, errors: result.error }
}

export function validateEvidenceIntegrity(cig: CIG): string[] {
  const errors: string[] = []
  const sourceIds = new Set(cig.sources.map((s: any) => s.id))
  
  // Check all claims have valid evidence anchors
  for (const claim of cig.claims) {
    for (const anchor of claim.evidence_anchors) {
      if (!sourceIds.has(anchor.source_id)) {
        errors.push(`Claim "${claim.id}" references non-existent source "${anchor.source_id}"`)
      }
    }
  }
  
  // Check all metrics have valid evidence anchors
  for (const metric of cig.metrics) {
    for (const anchor of metric.evidence_anchors) {
      if (!sourceIds.has(anchor.source_id)) {
        errors.push(`Metric "${metric.id}" references non-existent source "${anchor.source_id}"`)
      }
    }
  }
  
  // Check entity references in relationships
  const entityIds = new Set(cig.entities.map((e: any) => e.id))
  for (const rel of cig.relationships) {
    if (!entityIds.has(rel.source_entity_id)) {
      errors.push(`Relationship "${rel.id}" references non-existent source entity "${rel.source_entity_id}"`)
    }
    if (!entityIds.has(rel.target_entity_id)) {
      errors.push(`Relationship "${rel.id}" references non-existent target entity "${rel.target_entity_id}"`)
    }
  }
  
  return errors
}



